import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { DownloadSimple, Copy, Key, Tray } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, download, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";

export const GlassCard = ({ className = "", children, hover = false, ...rest }) => (
  <div className={`glass rounded-3xl ${hover ? "glass-hover" : ""} ${className}`} {...rest}>
    {children}
  </div>
);

export function TiltCard({ children, className = "", ...rest }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rx = useSpring(useTransform(y, [-0.5, 0.5], [7, -7]), { stiffness: 200, damping: 18 });
  const ry = useSpring(useTransform(x, [-0.5, 0.5], [-7, 7]), { stiffness: 200, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width - 0.5);
    y.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };
  return (
    <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }} onMouseMove={onMove} onMouseLeave={reset}
      className={`glass glass-hover rounded-3xl relative overflow-hidden ${className}`} {...rest}>
      {children}
    </motion.div>
  );
}

const TONES = {
  blue: "from-blue-500/25 text-blue-300",
  orange: "from-orange-500/25 text-orange-300",
  green: "from-emerald-500/25 text-emerald-300",
  rose: "from-rose-500/25 text-rose-300",
};

export function StatCard({ icon: Icon, label, value, hint, tone = "blue", testid, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }} className="h-full">
      <TiltCard className="p-6 h-full" data-testid={testid}>
        <div className={`absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br ${TONES[tone]} to-transparent blur-2xl`} />
        <div className="relative flex min-h-10 items-start justify-between">
          <span className="eyebrow">{label}</span>
          {Icon && (
            <span className={`grid h-10 w-10 place-items-center rounded-2xl bg-white/5 border border-white/10 ${TONES[tone].split(" ")[1]}`}>
              <Icon size={20} weight="duotone" />
            </span>
          )}
        </div>
        <div className="relative mt-6 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white" data-testid={testid ? `${testid}-value` : undefined}>
          {value}
        </div>
        {hint && <p className="relative mt-2 text-xs text-zinc-400">{hint}</p>}
      </TiltCard>
    </motion.div>
  );
}

export const PageHeader = ({ eyebrow, title, subtitle, actions }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
    className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div>
      {eyebrow && <div className="eyebrow mb-3">{eyebrow}</div>}
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-none text-white" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-3 text-sm md:text-base text-zinc-400 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </motion.div>
);

export const Modal = ({ open, onOpenChange, title, description, children, testid, wide }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid={testid} className={`glass-strong rounded-3xl border-white/10 text-white max-h-[90vh] overflow-y-auto [&>*]:min-w-0 ${wide ? "sm:max-w-3xl" : "sm:max-w-lg"}`}>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl tracking-tight">{title}</DialogTitle>
        {description && <DialogDescription className="text-zinc-400">{description}</DialogDescription>}
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="eyebrow mb-2 block">{label}</span>
    {children}
  </label>
);

const BADGE = {
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  pending: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  partial: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  P: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  A: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  L: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  published: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  draft: "bg-white/5 text-zinc-300 border-white/10",
  high: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  queued: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  sending: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  sent: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  delivered: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  read: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  failed: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  skipped: "bg-white/5 text-zinc-400 border-white/10",
  cancelled: "bg-white/5 text-zinc-400 border-white/10",
};

export const Badge = ({ status, children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${BADGE[status] || BADGE.draft}`}>
    {children || status}
  </span>
);

export const Empty = ({ title = "No records yet", hint }) => {
  const { t } = useT();
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white/5 border border-white/10 text-zinc-400 mb-4">
        <Tray size={26} weight="duotone" />
      </span>
      <p className="font-display text-lg text-white">{t(title)}</p>
      {hint && <p className="mt-1 text-sm text-zinc-500 max-w-sm">{hint}</p>}
    </div>
  );
};

export const Loader = () => (
  <div className="flex items-center justify-center py-20" data-testid="loader">
    <div className="h-10 w-10 rounded-full border-2 border-white/10 border-t-blue-400 animate-spin" />
  </div>
);

export const Table = ({ children, testid }) => (
  <div className="overflow-x-auto -mx-2">
    <table className="tbl w-full min-w-[640px]" data-testid={testid}>{children}</table>
  </div>
);

export const ReportSection = ({ icon: Icon, tone = "text-blue-300", title, subtitle, actions, children, testid, className = "" }) => (
  <motion.section initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45 }}
    className={`glass rounded-3xl p-5 sm:p-7 ${className}`} data-testid={testid}>
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3">
        {Icon && <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-white/5 border border-white/10 ${tone}`}><Icon size={20} weight="duotone" /></span>}
        <div>
          <h3 className="font-display text-base md:text-lg font-semibold text-white leading-tight">{title}</h3>
          {subtitle && <p className="mt-1 text-xs sm:text-sm text-zinc-400">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>}
    </div>
    {children}
  </motion.section>
);

const isNum = (v) => typeof v === "number" || (typeof v === "string" && /^[\d\s/.%—-]+$/.test(v));

export const DataTable = ({ head, rows, total, testid, minWidth = 0, groups }) => {
  const num = head.map((_, j) => j > 0 && rows.length > 0 && rows.every((r) => r[j] === "" || isNum(r[j])));
  const cls = (j, c) => (num[j] ? `num ${c === 0 || c === "0 / 0" ? "zero" : ""}` : j === 0 ? "lead" : "");
  return (
    <div className="rtbl-wrap">
      <table className="rtbl" style={{ minWidth }} data-testid={testid}>
        <thead>
          {groups && <tr className="rtbl-group">{groups.map(([l, span], i) => <th key={i} colSpan={span}>{l}</th>)}</tr>}
          <tr>{head.map((h, i) => <th key={i} className={num[i] ? "num" : ""}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className={cls(j, c)}>{c}</td>)}</tr>)}
        </tbody>
        {total && <tfoot><tr>{total.map((c, j) => <td key={j} className={num[j] ? "num" : ""}>{c}</td>)}</tr></tfoot>}
      </table>
    </div>
  );
};


function CredRow({ label, c }) {
  const copy = () => {
    navigator.clipboard?.writeText(`${c.email} / ${c.password || ""}`);
    toast.success("Copied");
  };
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
      <div className="eyebrow mb-2">{label}</div>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm min-w-0">
          <div className="text-white truncate" data-testid={`cred-${label.toLowerCase()}-email`}>{c.email}</div>
          <div className="text-zinc-400 font-mono" data-testid={`cred-${label.toLowerCase()}-password`}>
            {c.password || "Linked to existing parent account"}
          </div>
        </div>
        {c.password && (
          <button className="btn-ghost !px-3" onClick={copy} data-testid={`cred-${label.toLowerCase()}-copy`}>
            <Copy size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export function CredentialsDialog({ creds, onClose }) {
  const { t } = useT();
  return (
    <Modal open={!!creds} onOpenChange={(o) => !o && onClose()} title={t("Credentials")} testid="credentials-dialog"
      description="Share these login details securely. Passwords are shown only once.">
      <div className="space-y-3">
        {creds?.student && <CredRow label="Student" c={creds.student} />}
        {creds?.parent && <CredRow label="Parent" c={creds.parent} />}
        {creds?.email && <CredRow label="Login" c={creds} />}
        <div className="flex items-center gap-2 text-xs text-zinc-500"><Key size={14} /> Users can change their password after signing in.</div>
        <button className="btn-primary w-full" onClick={onClose} data-testid="credentials-close-btn">Done</button>
      </div>
    </Modal>
  );
}

export function ExportButtons({ entity }) {
  const { t } = useT();
  const run = async (fmt) => {
    try {
      await download(`/export/${entity}?format=${fmt}`, `${entity}.${fmt}`);
      toast.success(`${t("Export")} ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
      <button className="btn-ghost" onClick={() => run("csv")} data-testid={`export-${entity}-csv-btn`}><DownloadSimple size={16} /> CSV</button>
      <button className="btn-ghost" onClick={() => run("xlsx")} data-testid={`export-${entity}-xlsx-btn`}><DownloadSimple size={16} /> Excel</button>
    </>
  );
}

export function useChildren() {
  const [kids, setKids] = useState([]);
  const [sel, setSelState] = useState(() => localStorage.getItem("schola_child") || "");
  useEffect(() => {
    api.get("/my/children").then((r) => {
      setKids(r.data);
      if (!r.data.find((k) => k.id === localStorage.getItem("schola_child")) && r.data[0]) setSelState(r.data[0].id);
    }).catch(() => {});
  }, []);
  const setSel = (id) => {
    localStorage.setItem("schola_child", id);
    setSelState(id);
  };
  return { kids, sel: kids.find((k) => k.id === sel) ? sel : kids[0]?.id, setSel };
}

export function ChildPicker({ kids, sel, setSel }) {
  if (kids.length < 2) return null;
  return (
    <select className="field !w-auto !rounded-full" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="child-picker">
      {kids.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
    </select>
  );
}

export const fadeUp = (i = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: i * 0.05, duration: 0.4, ease: [0.2, 0.8, 0.2, 1] },
});
